import { LoadingConstants, useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import React from 'react';
import { zipSync } from 'fflate';
import BillingConstants from '../constants/BillingConstants';
import { emitDownloadInvoicesEvent } from '../model/ClickstreamEvents';
import { downloadBase64Data } from '../utils/downloadUtils';
import { AdproductsUserBillingInvoiceApiHandler_downloadInvoices } from '@zg-rentals/ts-adproducts-client';

const b64ToUint8Array = (b64: string): Uint8Array => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const uint8ArrayToB64 = (bytes: Uint8Array): string => {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

export const useInvoiceDownloader = () => {
  const [downloadState, setDownloadState] = React.useState<LoadingConstants>(LoadingConstants.INITIAL);
  const [downloadErrorMessage, setDownloadErrorMessage] = React.useState('');

  const { user } = useRentalManagerUser();
  const getInvoices = async (invoiceNumbers: Array<string>) => {
    emitDownloadInvoicesEvent({ user });
    setDownloadState(LoadingConstants.LOADING);

    try {
      const results = [];
      for (const invoiceNumber of invoiceNumbers) {
        const result = await AdproductsUserBillingInvoiceApiHandler_downloadInvoices({
          invoiceNumbers: [invoiceNumber],
        });
        results.push(result);
      }

      if (results.length === 1) {
        const { data, fileName } = results[0];
        downloadBase64Data(data, 'application/octet-stream', fileName);
      } else {
        const zipFiles: Record<string, Uint8Array> = {};
        for (const { data, fileName } of results) {
          zipFiles[fileName] = b64ToUint8Array(data);
        }
        const zipped = zipSync(zipFiles);
        const zipB64 = uint8ArrayToB64(zipped);
        downloadBase64Data(zipB64, 'application/octet-stream', 'invoices.zip');
      }

      setDownloadState(LoadingConstants.SUCCESS);
    } catch {
      setDownloadErrorMessage(BillingConstants.DOWNLOAD_ERROR_MESSAGE);
      setDownloadState(LoadingConstants.ERROR);
    }
  };

  const resetDownloadState = () => {
    setDownloadState(LoadingConstants.INITIAL);
    setDownloadErrorMessage('');
  };

  return React.useMemo(
    () => ({
      downloadState,
      downloadErrorMessage,
      getInvoices,
      resetDownloadState,
    }),
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
    [downloadState, downloadErrorMessage],
  );
};
